import { useState, useMemo, ChangeEvent, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import useSWR from 'swr';
import { ChevronLeft } from 'lucide-react';
import { Feedback } from '@/types/feedback';
import { getAccounts, getFeedbackByAccount, getFeedbackFacets, type AnalyticsRange, type AnalyticsFilters, type FeedbackFacets } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AccountCombobox } from '@/components/account/AccountCombobox';
import { SegmentedControl } from './SegmentedControl';
import { FeedbackFilters } from './FeedbackFilters';
import { FeedbackDashboard } from './FeedbackDashboard';
import { PortfolioOverview, type SentimentScope } from './overview/PortfolioOverview';
import { AccountComments } from './AccountComments';
import { trackFeedback } from '@/lib/rum';
import { type VoteKind } from '@/lib/vote';

interface FeedbackListProps {
  onViewDetails: (feedback: Feedback) => void;
}

export interface FeedbackFiltersState {
  feature: string;
  vote: string;
  rating: string;
  customer: string;
  user: string;
}

const initialFilters: FeedbackFiltersState = {
  feature: '',
  vote: 'all',
  rating: 'all',
  customer: '',
  user: '',
};

// Period presets (days). Single source of truth for both the analytics
// dashboard and the feedback list date window.
const PERIOD_OPTIONS = [7, 30, 90] as const;

// Derive a [startDate, endDate] window from a "last N days" preset: the N calendar
// days ending today (today included), matching the server's analytics window.
function rangeFromDays(days: number): { startDate: string; endDate: string } {
  const end = new Date();
  const start = new Date();
  start.setUTCDate(start.getUTCDate() - (days - 1));
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { startDate: fmt(start), endDate: fmt(end) };
}

// The selected period is either a "last N days" preset or an explicit range.
type Period =
  | { mode: 'preset'; days: number }
  | { mode: 'custom'; startDate: string; endDate: string };

function formatRangeLabel(start: string, end: string): string {
  if (!start || !end) return 'Custom range';
  const f = (d: string) => new Date(d + 'T00:00:00').toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  return `${f(start)} – ${f(end)}`;
}

// --- Shareable URL state: read period + filters from the query string ---

function readPeriodFromParams(params: URLSearchParams): Period {
  const from = params.get('from');
  const to = params.get('to');
  if (from && to) return { mode: 'custom', startDate: from, endDate: to };
  const days = Number(params.get('days'));
  return { mode: 'preset', days: (PERIOD_OPTIONS as readonly number[]).includes(days) ? days : 7 };
}

function readFiltersFromParams(params: URLSearchParams): FeedbackFiltersState {
  return {
    feature: params.get('feature') ?? '',
    customer: params.get('customer') ?? '',
    user: params.get('user') ?? '',
    vote: params.get('vote') ?? 'all',
    rating: params.get('rating') ?? 'all',
  };
}

export function FeedbackList({ onViewDetails }: FeedbackListProps) {
  const { instance } = useMsal();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedAccount, setSelectedAccount] = useState<string>(() => searchParams.get('account') || '');
  const [limit, setLimit] = useState<number>(100);
  const [period, setPeriod] = useState<Period>(() => readPeriodFromParams(searchParams));
  const [filters, setFilters] = useState<FeedbackFiltersState>(() => readFiltersFromParams(searchParams));
  const [showOnlyMine, setShowOnlyMine] = useState(false);
  // Portfolio sentiment scope for the Products-overview landing. Lifted here so it
  // sits in the shared filter strip; PortfolioOverview consumes it as a prop.
  const [overviewSentiment, setOverviewSentiment] = useState<SentimentScope>('all');

  const currentUserEmail = instance.getActiveAccount()?.username;

  // Persist account, period, and filters to the URL so the view is shareable.
  // URLSearchParams encodes values (e.g. customers with spaces).
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedAccount) params.set('account', selectedAccount);
    if (period.mode === 'custom') {
      if (period.startDate) params.set('from', period.startDate);
      if (period.endDate) params.set('to', period.endDate);
    } else if (period.days !== 7) {
      params.set('days', String(period.days));
    }
    if (filters.feature) params.set('feature', filters.feature);
    if (filters.customer) params.set('customer', filters.customer);
    if (filters.user) params.set('user', filters.user);
    if (filters.vote !== 'all') params.set('vote', filters.vote);
    if (filters.rating !== 'all') params.set('rating', filters.rating);
    setSearchParams(params, { replace: true });
  }, [selectedAccount, period, filters, setSearchParams]);

  // Period window drives both the analytics dashboard and the list fetch.
  const { startDate, endDate } = useMemo(
    () => (period.mode === 'preset'
      ? rangeFromDays(period.days)
      : { startDate: period.startDate, endDate: period.endDate }),
    [period]
  );

  const rangeValid =
    period.mode === 'preset' ||
    (!!period.startDate && !!period.endDate && period.startDate <= period.endDate);

  const analyticsRange: AnalyticsRange =
    period.mode === 'preset'
      ? { days: period.days }
      : { from: period.startDate, to: period.endDate };

  const periodLabel =
    period.mode === 'preset' ? `Last ${period.days} days` : formatRangeLabel(startDate, endDate);

  // Same filters that scope the list also scope the analytics charts/cards.
  const analyticsFilters = useMemo<AnalyticsFilters>(
    () => ({
      feature: filters.feature || undefined,
      customer: filters.customer || undefined,
      user: filters.user || undefined,
      vote: filters.vote !== 'all' ? filters.vote : undefined,
      rating: filters.rating !== 'all' ? Number(filters.rating) : undefined,
    }),
    [filters]
  );

  const { data: accounts, error: accountsError } = useSWR(
    'accounts',
    () => getAccounts(instance)
  );

  const filteredAccounts = useMemo(() => {
    if (!accounts) return [];
    if (showOnlyMine && currentUserEmail) {
      return accounts.filter(account => account.createdBy === currentUserEmail);
    }
    return accounts;
  }, [accounts, showOnlyMine, currentUserEmail]);

  const shouldFetchFeedback = selectedAccount !== '' && rangeValid;
  const {
    data: feedbackData,
    error: feedbackError,
    isLoading: isFeedbackLoading,
    mutate,
  } = useSWR(
    shouldFetchFeedback ? `feedback-${selectedAccount}-${limit}-${startDate}-${endDate}` : null,
    () => getFeedbackByAccount(instance, selectedAccount, limit, startDate, endDate)
  );

  // Distinct filter values for the period, powering the lookup dropdowns.
  const { data: facets } = useSWR<FeedbackFacets>(
    shouldFetchFeedback ? `facets-${selectedAccount}-${startDate}-${endDate}` : null,
    () => getFeedbackFacets(instance, selectedAccount, analyticsRange)
  );

  const handleAccountChange = (value: string) => {
    setSelectedAccount(value);
    setFilters(initialFilters);
    if (value) {
      trackFeedback.accountSelected(value);
    }
  };

  const handleLimitChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (value > 0) {
      setLimit(value);
    }
  };

  const handleFilterChange = (newFilters: Partial<FeedbackFiltersState>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    trackFeedback.filtered(newFilters);
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
  };

  // Drill-down: clicking a feature in the dashboard filters to it.
  const handleFeatureSelect = (feature: string) => {
    handleFilterChange({ feature });
  };

  // Click a sentiment badge to filter everything to that sentiment; click the
  // active one again to clear it. Maps to the shared 'vote' (sentiment) filter.
  const handleSentimentSelect = (kind: VoteKind) => {
    handleFilterChange({ vote: filters.vote === kind ? 'all' : kind });
  };

  // Loading accounts
  if (!accounts && !accountsError) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  // Error loading accounts
  if (accountsError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Failed to load accounts. Please try again.
          <Button variant="link" className="ml-2 p-0 h-auto" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {/* Title shown only on the overview (no account selected); the per-account
          view relies on the account control + the bottom back link to return. */}
      {!selectedAccount && (
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Products overview</h2>
          <p className="text-sm text-muted-foreground">
            {periodLabel} · {showOnlyMine ? 'your accounts' : 'all accounts'} — select an account to drill in
          </p>
        </div>
      )}

      {/* Filter strip: context (row 1: account + scope) over view controls
          (row 2: sentiment + time range), split by a thin rule. All three
          selectors share the SegmentedControl style. Wraps gracefully on narrow
          widths — the range control drops below the sentiment control. */}
      <div className="space-y-3">
        {/* Row 1 — context: which accounts am I looking at */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-[260px] flex-1">
            <AccountCombobox
              accounts={filteredAccounts}
              value={selectedAccount}
              onValueChange={handleAccountChange}
              placeholder="Search and select an account..."
            />
          </div>

          <SegmentedControl
            ariaLabel="Account scope"
            value={showOnlyMine ? 'mine' : 'all'}
            onChange={(v) => setShowOnlyMine(v === 'mine')}
            options={[
              { value: 'all', label: 'All accounts' },
              { value: 'mine', label: 'My accounts' },
            ]}
          />

          {selectedAccount && (
            <div className="flex items-center gap-2">
              <Label htmlFor="limit" className="text-xs text-muted-foreground">
                Limit
              </Label>
              <Input
                id="limit"
                type="number"
                value={limit}
                onChange={handleLimitChange}
                min="1"
                max="1000"
                className="h-8 w-20"
              />
            </div>
          )}
        </div>

        {/* Thin rule separating context from view controls */}
        <div className="border-t" />

        {/* Row 2 — view controls: how am I slicing it */}
        <div className="flex flex-wrap items-center gap-3">
          {!selectedAccount && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground">Sentiment</span>
              <SegmentedControl
                ariaLabel="Sentiment"
                value={overviewSentiment}
                onChange={setOverviewSentiment}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'up', label: 'Positive' },
                  { value: 'down', label: 'Negative' },
                ]}
              />
            </div>
          )}

          <div className="sm:ml-auto">
            <PeriodSelector period={period} range={{ startDate, endDate }} onChange={setPeriod} />
          </div>
        </div>
      </div>

      {/* Default landing state: cross-account portfolio overview. Selecting an
          account (here or from a row) drills into the per-account dashboard. */}
      {!selectedAccount &&
        (rangeValid ? (
          <PortfolioOverview
            range={analyticsRange}
            mine={showOnlyMine}
            sentiment={overviewSentiment}
            onSelectAccount={handleAccountChange}
          />
        ) : (
          <Alert>
            <AlertDescription>Select a start and end date to view the overview.</AlertDescription>
          </Alert>
        ))}

      {/* Per-account dashboard + feedback table (when an account is selected) */}
      {selectedAccount && (
        <>
          {!rangeValid ? (
            <Alert>
              <AlertDescription>Select a start and end date to view feedback.</AlertDescription>
            </Alert>
          ) : (
            <>
          <FeedbackFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            featureOptions={facets?.features ?? []}
            customerOptions={facets?.customers ?? []}
            userOptions={facets?.users ?? []}
          />

          <FeedbackDashboard
            accountToken={selectedAccount}
            range={analyticsRange}
            startDate={startDate}
            endDate={endDate}
            filters={analyticsFilters}
            onFeatureSelect={handleFeatureSelect}
            onSentimentSelect={handleSentimentSelect}
          />

          {/* Loading state */}
          {isFeedbackLoading && (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          )}

          {/* Error state */}
          {feedbackError && (
            <Alert variant="destructive">
              <AlertDescription>
                Failed to load feedback data.
                <Button variant="link" className="ml-2 p-0 h-auto" onClick={() => mutate()}>
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* Per-account comments explorer (search / sort / sentiment + feature / views) */}
          {!isFeedbackLoading && feedbackData && (
            <AccountComments feedback={feedbackData} onOpen={onViewDetails} filters={filters} />
          )}
            </>
          )}

          {/* Bottom back link, so users don't have to scroll up on long pages. */}
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleAccountChange('')}
              className="w-fit text-muted-foreground"
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              Back
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function PeriodSelector({
  period,
  range,
  onChange,
}: {
  period: Period;
  range: { startDate: string; endDate: string };
  onChange: (period: Period) => void;
}) {
  const isCustom = period.mode === 'custom';
  const rangeOptions = [
    ...PERIOD_OPTIONS.map((d) => ({ value: String(d), label: `${d}d` })),
    { value: 'custom', label: 'Custom' },
  ];
  const rangeValue = isCustom ? 'custom' : String(period.mode === 'preset' ? period.days : '');
  const handleRangeChange = (value: string) => {
    if (value === 'custom') {
      onChange({ mode: 'custom', startDate: range.startDate, endDate: range.endDate });
    } else {
      onChange({ mode: 'preset', days: Number(value) });
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <SegmentedControl
        ariaLabel="Time period"
        value={rangeValue}
        options={rangeOptions}
        onChange={handleRangeChange}
      />

      {isCustom && (
        <div className="flex items-center gap-2">
          <Input
            type="date"
            aria-label="Start date"
            value={period.startDate}
            max={period.endDate || undefined}
            onChange={(e) => onChange({ mode: 'custom', startDate: e.target.value, endDate: period.endDate })}
            className="h-8 w-[150px] text-sm"
          />
          <span className="text-sm text-muted-foreground">→</span>
          <Input
            type="date"
            aria-label="End date"
            value={period.endDate}
            min={period.startDate || undefined}
            onChange={(e) => onChange({ mode: 'custom', startDate: period.startDate, endDate: e.target.value })}
            className="h-8 w-[150px] text-sm"
          />
        </div>
      )}
    </div>
  );
}
